import LinkIcon from "@mui/icons-material/Link";
import { Box, Popover, TextField, Tooltip, Typography } from "@mui/material";
import type { Editor } from "@tiptap/react";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { CoAppActionButton, CoAppCancelTextButton, CoAppDestructiveActionButton } from "../../../styled/global.styled";
import MenuButton from "../MenuButton";

import { type LinkDraft, captureLinkDraft, normalizeHref } from "./link-utils";

interface LinkPopoverProps {
	editor: Editor | null;
	onLinkPopoverOpen?: () => void;
}

const LinkPopover = ({ editor, onLinkPopoverOpen }: LinkPopoverProps) => {
	const [linkUrl, setLinkUrl] = useState("");
	const [linkText, setLinkText] = useState("");
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [originalUrl, setOriginalUrl] = useState("");
	const [originalText, setOriginalText] = useState("");
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const popoverOpenRef = useRef(false);
	const draftRef = useRef<LinkDraft | null>(null);
	const anchorRef = useRef<HTMLSpanElement>(null);

	const applyDraftToState = useCallback((draft: LinkDraft) => {
		draftRef.current = draft;
		setLinkText(draft.snapshotText);
		setOriginalText(draft.snapshotText);
		setLinkUrl(draft.href);
		setOriginalUrl(draft.href);
		setIsEditing(draft.isEditing);
	}, []);

	const syncFromEditorSelection = useCallback(() => {
		if (!editor) {
			return;
		}

		try {
			applyDraftToState(captureLinkDraft(editor));
		} catch (error) {
			console.error("Error in selection update handler:", error);
		}
	}, [applyDraftToState, editor]);

	useEffect(() => {
		if (!editor) {
			return;
		}

		const handleUpdate = () => {
			if (popoverOpenRef.current) {
				return;
			}
			syncFromEditorSelection();
		};

		editor.on("selectionUpdate", handleUpdate);

		return () => {
			editor.off("selectionUpdate", handleUpdate);
		};
	}, [editor, syncFromEditorSelection]);

	const handleOpen = () => {
		if (!editor || !anchorRef.current) {
			return;
		}

		try {
			setTooltipOpen(false);
			popoverOpenRef.current = true;
			const draft = captureLinkDraft(editor);
			applyDraftToState(draft);
			setAnchorEl(anchorRef.current);
			onLinkPopoverOpen?.();
		} catch (error) {
			popoverOpenRef.current = false;
			console.error("Error in handleOpen:", error);
		}
	};

	const handleTriggerMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setTooltipOpen(false);
	};

	const handleAnchorMouseEnter = () => {
		if (!popoverOpenRef.current) {
			setTooltipOpen(true);
		}
	};

	const handleAnchorMouseLeave = () => {
		setTooltipOpen(false);
	};

	const handleClose = () => {
		popoverOpenRef.current = false;
		setTooltipOpen(false);
		setAnchorEl(null);
		setLinkUrl("");
		setLinkText("");
		setOriginalUrl("");
		setOriginalText("");
		setIsEditing(false);
		draftRef.current = null;
	};

	const stopEditorFocusSteal = (event: MouseEvent) => {
		event.stopPropagation();
	};

	const addOrUpdateLink = () => {
		if (!editor || !linkUrl.trim()) {
			return;
		}

		const href = normalizeHref(linkUrl);
		const draft = draftRef.current;
		const from = draft?.from ?? editor.state.selection.from;
		const to = draft?.to ?? editor.state.selection.to;
		const snapshotText = draft?.snapshotText ?? editor.state.doc.textBetween(from, to);
		const textToLink = linkText.trim() || snapshotText;

		if (!textToLink) {
			return;
		}

		try {
			const currentRangeText = editor.state.doc.textBetween(from, to);
			let success = false;

			if (currentRangeText === textToLink) {
				success = editor
					.chain()
					.focus()
					.setTextSelection({ from, to })
					.setLink({ href })
					.run();
			} else {
				success = editor
					.chain()
					.focus()
					.setTextSelection({ from, to })
					.deleteRange({ from, to })
					.insertContent(textToLink)
					.setTextSelection({ from, to: from + textToLink.length })
					.setLink({ href })
					.run();
			}

			if (!success) {
				return;
			}
		} catch (error) {
			console.error("Error in addOrUpdateLink:", error);
			return;
		}

		handleClose();
	};

	const removeLink = () => {
		if (!editor) {
			return;
		}

		try {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
		} catch (error) {
			console.error("Error in removeLink:", error);
		}

		handleClose();
	};

	const hasChanges = () => linkUrl !== originalUrl || linkText !== originalText;
	const popoverOpen = Boolean(anchorEl);
	const isTooltipVisible = tooltipOpen && !popoverOpen;

	return (
		<>
			<span
				ref={anchorRef}
				style={{ display: "inline-flex" }}
				onMouseEnter={handleAnchorMouseEnter}
				onMouseLeave={handleAnchorMouseLeave}
			>
				<Tooltip
					title="Insert Link"
					open={isTooltipVisible}
					disableHoverListener
					disableFocusListener
					disableTouchListener
				>
					<MenuButton
						isActive={editor?.isActive("link")}
						onMouseDown={handleTriggerMouseDown}
						onClick={handleOpen}
					>
						<LinkIcon fontSize="small" />
					</MenuButton>
				</Tooltip>
			</span>

			<Popover
				open={popoverOpen}
				anchorEl={anchorEl}
				onClose={handleClose}
				disableRestoreFocus
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: { zIndex: 1500 },
						onMouseDown: stopEditorFocusSteal,
					},
				}}
			>
				<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, width: "600px" }}>
					{isEditing && (
						<Typography variant="caption" sx={{ color: "text.secondary" }}>
							Editing existing link
						</Typography>
					)}

					<TextField
						label="Link Text"
						size="small"
						value={linkText}
						onChange={(e) => setLinkText(e.target.value)}
						onMouseDown={stopEditorFocusSteal}
						autoFocus
					/>

					<TextField
						label="Link URL"
						size="small"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						onMouseDown={stopEditorFocusSteal}
						onKeyDown={(e) => e.key === "Enter" && addOrUpdateLink()}
						placeholder="https://example.com"
					/>

					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Box>
							<CoAppDestructiveActionButton
								onClick={removeLink}
								size="small"
								sx={{
									display: isEditing ? "inline-flex" : "none",
									padding: "4px 12px",
								}}
							>
								Remove Link
							</CoAppDestructiveActionButton>
						</Box>

						<Box sx={{ display: "flex", gap: 1 }}>
							<CoAppCancelTextButton onClick={handleClose} size="small">
								Cancel
							</CoAppCancelTextButton>

							<CoAppActionButton
								onClick={addOrUpdateLink}
								disabled={!linkUrl.trim() || (isEditing && !hasChanges())}
								size="small"
								sx={{ padding: "4px 12px" }}
							>
								{isEditing ? "Update" : "Add"}
							</CoAppActionButton>
						</Box>
					</Box>
				</Box>
			</Popover>
		</>
	);
};

export default LinkPopover;
